import { formatFirstName, getFullName } from '@models/User';
import { searchItemTypes } from '@constants/index';
import { compareDates, getFullDate } from '@utils/dates';
import { pluralize } from '@utils/textHelper';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';

const separator = ' • ';

export const getAuthorFullName = (author) =>
  getFullName(formatFirstName(author?.firstName || ''), author?.lastName || '');

const getFormattedCount = (count, text) =>
  count > 0 ? pluralize(count, text) : '';

const getUserSubText = (item) => {
  const { followersCount, coursesCount, isExEducator, educatorTypeV2 } =
    item?.data || {};

  if (isExEducator) return 'Ex-educator';
  if (educatorTypeV2 === EDUCATOR_TYPES.PARTNER) return 'Partner Educator';
  return `${getFormattedCount(followersCount, 'follower')}${
    followersCount > 0 && coursesCount > 0 ? separator : ''
  }${getFormattedCount(coursesCount, 'course')}`;
};

const itemTypeFormatter = {
  [searchItemTypes.COURSE]: (item) => ({
    as: item?.data?.programme?.relativeLink,
    thumbnail:
      item?.data?.programme?.coverPhotoV1 || item?.data?.programme?.coverPhoto,
    name: item?.data?.name,
    subText: `By ${getAuthorFullName(item?.data?.author)}`
  }),
  [searchItemTypes.TEST_SERIES]: (item) => {
    const dateNow = new Date().getTime();
    const { endsAt, startsAt } = item?.data?.programme || {};
    const dateString =
      compareDates(endsAt, dateNow) === -1
        ? `Ended on ${getFullDate(endsAt)}`
        : `${
            compareDates(startsAt, dateNow) === -1 ? 'Started' : 'Starts'
          } on ${getFullDate(startsAt)}`;
    return {
      as: item?.data?.programme?.relativeLink,
      thumbnail:
        item?.data?.programme?.coverPhotoV1 ||
        item?.data?.programme?.coverPhoto,
      colorCode: item?.data?.programme?.colorCode,
      name: item?.data?.programme?.name,
      subText: `${pluralize(
        item?.data?.programme?.itemCount,
        'test'
      )}${separator}${dateString}`
    };
  },
  [searchItemTypes.PLUS_LESSON]: (item) => {
    const author =
      item?.data?.lesson?.author || item?.data?.lesson?.programme?.author || {};
    return {
      as: item?.data?.lesson?.relativeLink,
      thumbnail:
        item?.data?.lesson?.programme?.coverPhotoV1 ||
        item?.data?.lesson?.programme?.coverPhoto,
      name: item?.data?.lesson?.name,
      subText: `By ${getAuthorFullName(author)}`
    };
  },
  [searchItemTypes.USER]: (item) => {
    return {
      as: item?.data?.relativeLink,
      thumbnail: item?.data?.avatarV1 || item?.data?.avatar,
      name: getAuthorFullName(item?.data),
      subText: getUserSubText(item)
    };
  },
  [searchItemTypes.TOPOLOGY]: (item) => {
    const { coursesCount } = item?.data || {};
    return {
      as: item?.data?.relativeLink,
      thumbnail: item?.data?.coverPhotoV1 || null,
      name: item?.data?.name,
      subText: `${item?.data?.parents?.[0] || ''}${
        coursesCount > 0 ? separator : ''
      }${getFormattedCount(coursesCount, 'course')}`
    };
  },
  [searchItemTypes.BATCH]: (item) => ({
    as: `/batch/${item?.data.slug}/${item?.data.uid}`,
    thumbnail: item?.data.coverPhotoV1 || null,
    name: item?.data.name,
    subText: `By ${getAuthorFullName(item?.data.authors[0])} & ${
      item?.data.authors.length - 1
    } more`
  })
};

const searchItemsTypesValues = Object.values(searchItemTypes);

const processRecentSearchesData = (result) => {
  try {
    return (result?.blocks?.[0]?.items || [])
      .filter((item) => searchItemsTypesValues.includes(item?.type))
      .map((item) => ({
        ...item,
        info: itemTypeFormatter[item?.type](item)
      }));
  } catch {
    return [];
  }
};

export default processRecentSearchesData;
